import type { ColumnMeta, Row } from '@/lib/biData';

// ============================================================
// Zifek BI — aperçu des données : qualité des colonnes + extrait
// des premières lignes.
// ============================================================

const TYPE_META: Record<string, { label: string; cls: string }> = {
  number: { label: 'Nombre', cls: 'bg-primary-100 text-primary-700' },
  date: { label: 'Date', cls: 'bg-accent-100 text-accent-700' },
  boolean: { label: 'Oui/Non', cls: 'bg-secondary-100 text-secondary-700' },
  string: { label: 'Texte', cls: 'bg-background-200/60 text-foreground-600' },
};

export default function DataPreview({
  columns,
  rows,
  datasetName,
}: {
  columns: ColumnMeta[];
  rows: Row[];
  datasetName: string;
}) {
  const totalNull = columns.reduce((s, c) => s + c.nullCount, 0);
  const totalCells = columns.length * rows.length;
  const quality = totalCells > 0 ? Math.round(((totalCells - totalNull) / totalCells) * 100) : 100;

  return (
    <div className="space-y-5">
      {/* Bandeau qualité */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Lignes', value: rows.length },
          { label: 'Colonnes', value: columns.length },
          { label: 'Cellules vides', value: totalNull },
          { label: 'Complétude', value: `${quality}%` },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <p className="text-[11px] text-foreground-500">{s.label}</p>
            <p className="text-xl font-bold font-heading text-foreground-950">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Colonnes */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-list-check-2 text-primary-500"></i>Colonnes détectées — {datasetName}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/50">
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-foreground-500">Colonne</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-foreground-500">Type</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-foreground-500">Vides</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-foreground-500">Distincts</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-foreground-500">Exemples</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((c) => {
                const meta = TYPE_META[c.type] || TYPE_META.string;
                return (
                  <tr key={c.name} className="border-b border-background-200/70 last:border-0">
                    <td className="px-3 py-2.5 text-xs font-medium text-foreground-800">{c.name}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-foreground-600">{c.nullCount}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-foreground-600">{c.uniqueCount}</td>
                    <td className="px-3 py-2.5 text-xs text-foreground-400 truncate max-w-[220px]">{c.sampleValues.join(', ') || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Extrait */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-table-line text-accent-500"></i>Aperçu (20 premières lignes)
        </h3>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-background-100">
              <tr>
                {columns.map((c) => (
                  <th key={c.name} className="text-left px-3 py-2 font-semibold text-foreground-500 whitespace-nowrap border-b border-background-200/70">{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((r, i) => (
                <tr key={i} className="border-b border-background-200/70 last:border-0">
                  {columns.map((c) => (
                    <td key={c.name} className="px-3 py-2 text-foreground-700 whitespace-nowrap max-w-[200px] truncate">
                      {r[c.name] === null || r[c.name] === undefined ? <span className="text-foreground-300">—</span> : String(r[c.name])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}